import { DEFAULT_LANGUAGE, type SupportedLanguage } from '@core/i18n/types';

export type BillType = 'boleta' | 'factura';

/**
 * Every receipt name is written in all supported languages, so adding a
 * language to SupportedLanguage makes the table below fail to compile until
 * the new translations are filled in.
 */
type Localized = Record<SupportedLanguage, string>;

export interface ReceiptCountryLabels {
  /** Identity document for a receipt issued to a person. Never translated. */
  personalDocument: string;
  /** Tax identifier for a receipt issued to a company. Never translated. */
  businessDocument: string;
  personalReceipt: Localized;
  businessReceipt: Localized;
}

const RECEIPT_LANGS: readonly SupportedLanguage[] = ['de', 'en', 'es', 'fr', 'pt'];

/**
 * Receipt names are ordinary nouns and get translated. Document names are legal
 * identifiers and never do: a guest booking in Chile must read "RUT" in every
 * language, because that is what their ID card says.
 *
 * In a language other than the country's own, the local term is kept and a
 * translated gloss follows it, so the guest both recognises the word the
 * restaurant will use and understands what they are choosing.
 *
 * `boleta`/`factura` stay as the internal billType regardless of the labels
 * shown here — see BILL_TYPE_CONTRACT below.
 */
const RECEIPT_LABELS: Record<string, ReceiptCountryLabels> = {
  // SUNAT: boleta de venta (persona) / factura (empresa).
  PE: {
    personalDocument: 'DNI',
    businessDocument: 'RUC',
    personalReceipt: { es: 'Boleta', en: 'Boleta (personal receipt)', pt: 'Boleta (recibo pessoal)', fr: 'Boleta (reçu personnel)', de: 'Boleta (Privatbeleg)' },
    businessReceipt: { es: 'Factura', en: 'Factura (business invoice)', pt: 'Factura (fatura empresarial)', fr: 'Factura (facture entreprise)', de: 'Factura (Firmenrechnung)' },
  },
  // SII: boleta / factura, and the RUT identifies people and companies alike.
  CL: {
    personalDocument: 'RUT',
    businessDocument: 'RUT',
    personalReceipt: { es: 'Boleta', en: 'Boleta (personal receipt)', pt: 'Boleta (recibo pessoal)', fr: 'Boleta (reçu personnel)', de: 'Boleta (Privatbeleg)' },
    businessReceipt: { es: 'Factura', en: 'Factura (business invoice)', pt: 'Factura (fatura empresarial)', fr: 'Factura (facture entreprise)', de: 'Factura (Firmenrechnung)' },
  },
  // ARCA/AFIP: the letter (A/B/C) is decided by the issuing venue's own tax
  // standing, not by the guest — a monotributista venue only ever issues C,
  // whoever the customer is, while a responsable inscripto issues A to
  // companies and B to consumers. Meitre does not know which the venue is, and
  // the guest has no say in it either way, so the labels ask the one thing the
  // guest does decide: whose name the invoice carries.
  AR: {
    personalDocument: 'DNI',
    businessDocument: 'CUIT',
    personalReceipt: { es: 'Factura a mi nombre', en: 'Invoice in my name', pt: 'Fatura em meu nome', fr: 'Facture à mon nom', de: 'Rechnung auf meinen Namen' },
    businessReceipt: { es: 'Factura a nombre de una empresa', en: 'Invoice in a company name', pt: 'Fatura em nome de uma empresa', fr: "Facture au nom d'une entreprise", de: 'Rechnung auf einen Firmennamen' },
  },
  // NFC-e and NF-e are the venue's document types, not what a guest asks for:
  // a diner asks for the nota "com CPF", and adding the CPF does not even
  // change the document type. NF-e only enters when the invoice goes to a
  // company's CNPJ.
  BR: {
    personalDocument: 'CPF',
    businessDocument: 'CNPJ',
    personalReceipt: { pt: 'Nota em meu nome', es: 'Nota a mi nombre', en: 'Receipt in my name', fr: 'Note à mon nom', de: 'Beleg auf meinen Namen' },
    businessReceipt: { pt: 'Nota em nome de uma empresa', es: 'Nota a nombre de una empresa', en: 'Receipt in a company name', fr: "Note au nom d'une entreprise", de: 'Beleg auf einen Firmennamen' },
  },
  // SAT/CFDI 4.0: an RFC belongs to individuals too, so the split is who the
  // invoice is addressed to rather than person vs company in the legal sense.
  // Both sides collect an RFC. Offering an explicit "no invoice" option was
  // considered and dropped: the guest is already at the payment step, and
  // unrequested consumption rolls into the venue's global invoice anyway, so
  // the opt-out adds a decision without changing the outcome.
  MX: {
    personalDocument: 'RFC',
    businessDocument: 'RFC',
    personalReceipt: { es: 'Factura a mi nombre', en: 'Invoice in my name', pt: 'Fatura em meu nome', fr: 'Facture à mon nom', de: 'Rechnung auf meinen Namen' },
    businessReceipt: { es: 'Factura a nombre de una empresa', en: 'Invoice in a company name', pt: 'Fatura em nome de uma empresa', fr: "Facture au nom d'une entreprise", de: 'Rechnung auf einen Firmennamen' },
  },
  // DGI classifies these as e-Ticket (CFE 101) and e-Factura (CFE 111), but
  // nobody asks a waiter for an "e-Ticket" — you ask for a factura, and say
  // "con RUT" when it is for a company. The labels use what people say.
  UY: {
    personalDocument: 'CI',
    businessDocument: 'RUT',
    personalReceipt: { es: 'Ticket', en: 'Ticket (personal)', pt: 'Ticket (pessoal)', fr: 'Ticket (personnel)', de: 'Ticket (privat)' },
    businessReceipt: { es: 'Factura con RUT', en: 'Factura con RUT (business)', pt: 'Factura con RUT (empresarial)', fr: 'Factura con RUT (entreprise)', de: 'Factura con RUT (geschäftlich)' },
  },
  // DIAN: asking for the invoice with a NIT is what obliges the venue to issue
  // a full electronic invoice instead of the POS equivalent — so the guest does
  // drive this one, but they ask for "factura con NIT", not for a document type.
  CO: {
    personalDocument: 'CC',
    businessDocument: 'NIT',
    personalReceipt: { es: 'Factura a mi nombre', en: 'Invoice in my name', pt: 'Fatura em meu nome', fr: 'Facture à mon nom', de: 'Rechnung auf meinen Namen' },
    businessReceipt: { es: 'Factura con NIT', en: 'Invoice with NIT (company)', pt: 'Fatura com NIT (empresa)', fr: 'Facture avec NIT (entreprise)', de: 'Rechnung mit NIT (Firma)' },
  },
  // DNIT/SIFEN: there is no "boleta de venta" here, unlike Peru and Chile. The
  // only boletas in the decree are RESIMPLE (restricted to Régimen Simplificado
  // micro-taxpayers, which a restaurant is not) and public-event entry tickets.
  // A restaurant issues a factura either way; what changes is the buyer's id.
  PY: {
    personalDocument: 'CI',
    businessDocument: 'RUC',
    personalReceipt: { es: 'Factura a mi nombre', en: 'Invoice in my name', pt: 'Fatura em meu nome', fr: 'Facture à mon nom', de: 'Rechnung auf meinen Namen' },
    businessReceipt: { es: 'Factura con RUC', en: 'Invoice with RUC (company)', pt: 'Fatura com RUC (empresa)', fr: 'Facture avec RUC (entreprise)', de: 'Rechnung mit RUC (Firma)' },
  },
  // SRI: the nota de venta belongs to a tax regime, not to a kind of buyer —
  // resolution NAC-DGERCGC24-00000027 art. 6 authorises it "únicamente" for
  // RIMPE negocios populares (under USD 20k a year), and its official template
  // carries the "Contribuyente Negocio Popular Régimen RIMPE" legend. A venue
  // taking deposits issues a factura to a person and a company alike; only the
  // buyer's id changes, and a natural person with economic activity has a RUC
  // of their own.
  EC: {
    personalDocument: 'Cédula',
    businessDocument: 'RUC',
    personalReceipt: { es: 'Factura a mi nombre', en: 'Invoice in my name', pt: 'Fatura em meu nome', fr: 'Facture à mon nom', de: 'Rechnung auf meinen Namen' },
    businessReceipt: { es: 'Factura con RUC', en: 'Invoice with RUC (company)', pt: 'Fatura com RUC (empresa)', fr: 'Facture avec RUC (entreprise)', de: 'Rechnung mit RUC (Firma)' },
  },
  // SIN: both are "factura"; only the buyer's NIT differs, so the labels say so
  // rather than showing the same word on both buttons.
  BO: {
    personalDocument: 'CI',
    businessDocument: 'NIT',
    personalReceipt: { es: 'Factura sin NIT', en: 'Factura without NIT', pt: 'Factura sem NIT', fr: 'Factura sans NIT', de: 'Factura ohne NIT' },
    businessReceipt: { es: 'Factura con NIT', en: 'Factura with NIT', pt: 'Factura com NIT', fr: 'Factura avec NIT', de: 'Factura mit NIT' },
  },
  // SAT/FEL: decreto 31-2024 removed the anonymous consumer invoice, so a NIT is
  // always requested; the personal case keeps its "consumidor final" wording.
  GT: {
    personalDocument: 'DPI/CUI',
    businessDocument: 'NIT',
    personalReceipt: { es: 'Factura (consumidor final)', en: 'Factura (final consumer)', pt: 'Factura (consumidor final)', fr: 'Factura (consommateur final)', de: 'Factura (Endverbraucher)' },
    businessReceipt: { es: 'Factura con NIT', en: 'Factura with NIT', pt: 'Factura com NIT', fr: 'Factura avec NIT', de: 'Factura mit NIT' },
  },
  // Hacienda: asking for the invoice with a cédula is what obliges the venue to
  // issue a factura electrónica instead of a tiquete. Those are the document
  // names in the venue's system; the guest asks for a factura a su nombre.
  CR: {
    personalDocument: 'Cédula',
    businessDocument: 'Cédula jurídica',
    personalReceipt: { es: 'Factura a mi nombre', en: 'Invoice in my name', pt: 'Fatura em meu nome', fr: 'Facture à mon nom', de: 'Rechnung auf meinen Namen' },
    businessReceipt: { es: 'Factura a nombre de una empresa', en: 'Invoice in a company name', pt: 'Fatura em nome de uma empresa', fr: "Facture au nom d'une entreprise", de: 'Rechnung auf einen Firmennamen' },
  },
  // DGII: NCF type 32 (consumo) / type 31 (crédito fiscal).
  DO: {
    personalDocument: 'Cédula',
    businessDocument: 'RNC',
    personalReceipt: { es: 'Factura de consumo', en: 'Factura de consumo (personal)', pt: 'Factura de consumo (pessoal)', fr: 'Factura de consumo (personnelle)', de: 'Factura de consumo (privat)' },
    businessReceipt: { es: 'Factura de crédito fiscal', en: 'Factura de crédito fiscal (business)', pt: 'Factura de crédito fiscal (empresarial)', fr: 'Factura de crédito fiscal (entreprise)', de: 'Factura de crédito fiscal (geschäftlich)' },
  },
  // Hacienda: the naming is inverted against PE/CL — "factura" is the PERSONAL
  // document here, and the company one is the CCF. Do not "fix" this by analogy.
  SV: {
    personalDocument: 'DUI',
    businessDocument: 'NIT',
    personalReceipt: { es: 'Factura', en: 'Factura (personal)', pt: 'Factura (pessoal)', fr: 'Factura (personnelle)', de: 'Factura (privat)' },
    businessReceipt: { es: 'Comprobante de crédito fiscal (CCF)', en: 'Comprobante de crédito fiscal (CCF)', pt: 'Comprobante de crédito fiscal (CCF)', fr: 'Comprobante de crédito fiscal (CCF)', de: 'Comprobante de crédito fiscal (CCF)' },
  },
};

/**
 * The value sent to the backend as `billType`, for every country. It means
 * person vs company, is not display text, and is never renamed to match a local
 * receipt name — the backend flattens it into a free-text billing_notes column,
 * so a country-specific value would leave that column holding fourteen
 * different vocabularies with no way to group them afterwards.
 */
export const BILL_TYPE_CONTRACT: Record<'personal' | 'business', BillType> = {
  personal: 'boleta',
  business: 'factura',
};

export function normalizeReceiptLang(lang?: string | null): SupportedLanguage {
  const base = (lang ?? '').trim().toLowerCase().split('-')[0] as SupportedLanguage;

  return RECEIPT_LANGS.includes(base) ? base : DEFAULT_LANGUAGE;
}

export function hasReceiptLabels(country?: string | null): boolean {
  return Boolean(country && country in RECEIPT_LABELS);
}

/**
 * Country-specific labels, or null when the country has no entry — the caller
 * then falls back to the generic i18n keys, which read correctly anywhere.
 */
export function getReceiptLabels(country?: string | null): ReceiptCountryLabels | null {
  return country ? RECEIPT_LABELS[country] ?? null : null;
}
